"use client";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getStoredAuth } from "../../../utils/authStorage";
import { fetchMyBillingOverview } from "../../../services/billingService";
import { extractApiErrorMessage } from "../../../utils/error-handler";
import { formatBillingDate, getSubscriptionGraceInfo } from "../../../utils/billingFormat";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Receipt, FileText } from "lucide-react";
import PlanSelection from "./PlanSelection";

const BillingContent = ({ standalone = false, onViewPayments, onViewInvoices } = {}) => {
  const { user } = getStoredAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notApplicable, setNotApplicable] = useState(false);

  const load = () => {
    if (user?.role !== "schooladmin") {
      if (standalone) {
        setNotApplicable(true);
        return Promise.resolve();
      }
      navigate("/dashboard", { replace: true });
      return Promise.resolve();
    }
    return fetchMyBillingOverview()
      .then((res) => {
        const data = res.data;
        if (!data || !data.applicable || data.billingVisible === false) {
          if (standalone) {
            setNotApplicable(true);
            return;
          }
          navigate("/dashboard", { replace: true });
          return;
        }
        setOverview(data);
      })
      .catch((err) => setError(extractApiErrorMessage(err)));
  };

  useEffect(() => {
    load().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  if (notApplicable) {
    return <div className="p-6 text-sm text-gray-500">Billing isn&apos;t available for this account.</div>;
  }

  if (!standalone && (user?.role !== "schooladmin" || (!loading && (!overview || !overview.applicable || overview.billingVisible === false)))) {
    return null;
  }

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">Loading billing details...</div>;
  }

  if (error) {
    return <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700 text-sm">{error}</div>;
  }

  const { subscription, subscriptionChain = [], usage, billingExpiryBannerEnabled } = overview;
  const graceInfo = getSubscriptionGraceInfo(subscription);
  const isExpired = subscription?.status === "EXPIRED";
  const showExpiryBanner = isExpired && billingExpiryBannerEnabled !== false;
  const isActiveSub = subscription?.status === "ACTIVE";
  const isTrial = subscription?.type === "TRIAL";
  const daysLeft = subscription?.endDate
    ? Math.max(0, Math.ceil((new Date(subscription.endDate) - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;
  const atLimit = usage && usage.currentUsers >= usage.maxUsers && usage.maxUsers != null;
  const now = new Date();
  const isUpcoming = Boolean(subscription && subscriptionChain.length > 1 && new Date(subscription.startDate) > now);
  const currentIndex = subscriptionChain.findIndex(
    (s) => new Date(s.startDate) <= now && now <= new Date(s.endDate)
  );
  const currentEntry = isUpcoming ? subscriptionChain[currentIndex >= 0 ? currentIndex : 0] : null;
  const nextEntry = isUpcoming && currentIndex >= 0 ? subscriptionChain[currentIndex + 1] : null;
  const queuedAfterNext = nextEntry ? subscriptionChain.length - currentIndex - 2 : 0;
  const currentDaysLeft = currentEntry?.endDate
    ? Math.max(0, Math.ceil((new Date(currentEntry.endDate) - now) / (1000 * 60 * 60 * 24)))
    : 0;
  const planLabel = (entry) => (entry?.type === "TRIAL" ? "Trial" : entry?.plan?.name || "Plan");

  const displayEntry = isUpcoming ? currentEntry : subscription;
  const displayIsTrial = displayEntry?.type === "TRIAL";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Billing</h1>
          <p className="text-sm text-gray-500 mt-0.5">Your current plan and usage.</p>
        </div>
      </div>

      {!subscription && (
        <Alert>
          <AlertDescription>No active plan yet — choose a plan below to activate billing.</AlertDescription>
        </Alert>
      )}

      {isUpcoming ? (
        <Alert className="border-blue-300 bg-blue-50">
          <AlertDescription className="text-blue-800">
            🎁{" "}
            {currentEntry.type === "TRIAL" ? (
              <>You&apos;re on a free trial until {formatBillingDate(currentEntry.endDate)} ({currentDaysLeft} day{currentDaysLeft === 1 ? "" : "s"} left).</>
            ) : (
              <>Your current plan ({planLabel(currentEntry)}) runs until {formatBillingDate(currentEntry.endDate)} ({currentDaysLeft} day{currentDaysLeft === 1 ? "" : "s"} left).</>
            )}{" "}
            {nextEntry && (
              <>
                You&apos;ve already purchased {planLabel(nextEntry)} — it will start automatically on{" "}
                {formatBillingDate(nextEntry.startDate)} and run until {formatBillingDate(nextEntry.endDate)}.
                {queuedAfterNext > 0 && ` (+${queuedAfterNext} more plan${queuedAfterNext === 1 ? "" : "s"} queued after that)`}
              </>
            )}
          </AlertDescription>
        </Alert>
      ) : (
        isActiveSub && isTrial && (
          <Alert className="border-blue-300 bg-blue-50">
            <AlertDescription className="text-blue-800">
              🎁 You&apos;re on a free trial until {formatBillingDate(subscription.endDate)} ({daysLeft} day{daysLeft === 1 ? "" : "s"} left).
            </AlertDescription>
          </Alert>
        )
      )}

      {showExpiryBanner && graceInfo?.state === "IN_GRACE" && (
        <Alert className="border-amber-300 bg-amber-50">
          <AlertDescription className="text-amber-900">
            ⏰ Your subscription ended on <strong>{formatBillingDate(subscription.endDate)}</strong>. You are currently in a {graceInfo.graceDays}-day grace period ({graceInfo.daysLeft} day{graceInfo.daysLeft === 1 ? "" : "s"} remaining). Renew now to keep continuous uninterrupted access.
          </AlertDescription>
        </Alert>
      )}

      {showExpiryBanner && graceInfo?.state === "GRACE_EXPIRED" && (
        <Alert className="border-red-300 bg-red-50">
          <AlertDescription className="text-red-900">
            ⚠️ Your subscription and {graceInfo.graceDays}-day grace period expired on <strong>{formatBillingDate(subscription.endDate)}</strong>. Choose a plan below to reactivate your subscription.
          </AlertDescription>
        </Alert>
      )}

      {showExpiryBanner && graceInfo?.state === "TRIAL_EXPIRED" && (
        <Alert className="border-amber-300 bg-amber-50">
          <AlertDescription className="text-amber-900">
            Your free trial expired on <strong>{formatBillingDate(subscription.endDate)}</strong>. Choose a plan below to activate your account.
          </AlertDescription>
        </Alert>
      )}

      {atLimit && (
        <Alert className="border-amber-300 bg-amber-50">
          <AlertDescription className="text-amber-800">
            You have reached your staff limit ({usage.currentUsers}/{usage.maxUsers}). Contact support to increase your
            plan&apos;s user limit.
          </AlertDescription>
        </Alert>
      )}

      <PlanSelection hasExistingSubscription={Boolean(subscription)} onPaymentConfirmed={load} />

      {subscription && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-500 font-medium">Plan</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg font-bold text-gray-900">
                {displayIsTrial ? "Trial Access" : (displayEntry.plan?.name || "-")}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-500 font-medium">Status</CardTitle>
            </CardHeader>
            <CardContent>
              {isUpcoming ? (
                <Badge className="bg-blue-600 text-white hover:bg-blue-600" variant="default">
                  Upcoming
                </Badge>
              ) : graceInfo ? (
                <Badge className={graceInfo.badgeClass} variant={graceInfo.variant}>
                  {graceInfo.label}
                </Badge>
              ) : (
                <span className="text-sm text-gray-400">-</span>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-500 font-medium">Users</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg font-bold text-gray-900">
                {usage?.currentUsers ?? "-"} / {usage?.maxUsers ?? "-"}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-500 font-medium">Dates</CardTitle>
            </CardHeader>
            <CardContent>
              {isUpcoming ? (
                <div className="space-y-1">
                  {subscriptionChain.map((entry) => (
                    <p key={entry._id} className="text-xs text-gray-900">
                      {planLabel(entry)}: {formatBillingDate(entry.startDate)} → {formatBillingDate(entry.endDate)}
                    </p>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-900">
                  {formatBillingDate(displayEntry.startDate)} → {formatBillingDate(displayEntry.endDate)}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {!isTrial && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-500/10">
                <Receipt className="h-4 w-4 text-blue-600" />
              </span>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Payment History</h2>
                <p className="text-xs text-gray-500">All payments made toward your subscription.</p>
              </div>
            </div>
            <Button variant="outline" onClick={standalone ? onViewPayments : () => navigate("/dashboard/billing/payments")}>View</Button>
          </div>
          <div className="flex items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-green-500/10">
                <FileText className="h-4 w-4 text-green-600" />
              </span>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Invoice History</h2>
                <p className="text-xs text-gray-500">Invoices issued for your payments.</p>
              </div>
            </div>
            <Button variant="outline" onClick={standalone ? onViewInvoices : () => navigate("/dashboard/billing/invoices")}>View</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingContent;
